import { GrowthBook, InitResponse } from '@growthbook/growthbook';
import type { AbTestEvent } from '@sortlist-frontend/tracking/edge';
import { Environment as _Environment, DeepWriteable } from '@sortlist-frontend/utils';

import { config } from '../defaults';
import { ConfigOverride } from './overrides';

export enum Locale {
  en = 'en',
  fr = 'fr',
  de = 'de',
  nl = 'nl',
  it = 'it',
  es = 'es',
}

export type Domain =
  | 'www.sortlist.com'
  | 'www.sortlist.fr'
  | 'www.sortlist.de'
  | 'www.sortlist.nl'
  | 'www.sortlist.it'
  | 'www.sortlist.es'
  | 'www.sortlist.co.uk'
  | 'www.sortlist.it'
  | 'www.sortlist.ca'
  | 'www.sortlist.us'
  | 'www.sortlist.ch'
  | 'www.sortlist.be'
  | 'www.sortlist-test.com'
  | 'www-fr.sortlist-test.com'
  | 'www-de.sortlist-test.com'
  | 'www-es.sortlist-test.com'
  | 'www-nl.sortlist-test.com'
  | 'www-it.sortlist-test.com'
  | 'www-ch.sortlist-test.com'
  | 'www-ca.sortlist-test.com'
  | 'www-us.sortlist-test.com'
  | 'www-uk.sortlist-test.com'
  | 'www.sortlist-test.be'
  | 'localhost';

// All markets where Sortlist is active (and for which there is a matching domain)
export type Market = 'global' | 'fr' | 'nl' | 'de' | 'it' | 'es' | 'uk' | 'ca' | 'us' | 'ch' | 'be';

type RawAvailableFeatureToggles = DeepWriteable<typeof config>;
export type AvailableFeatureNames = keyof RawAvailableFeatureToggles;

export type AvailableFeatureToggles = {
  [Key in AvailableFeatureNames]: RawAvailableFeatureToggles[Key];
};

export type FlagValueString = `${string}-c` | `${string}-${string}`;

export type FeatureFlagValue = FlagValueString | ExperimentControlVariant;

export type ExperimentControlVariant = 'control';

type BaseExperiment = {
  key: string;
  /**
   * @deprecated
   * If true, it means the override from the cookies exists
   */
  isRunning?: boolean;
  // This is the gbuuid provided by Growthbook
  /**
   * @deprecated
   */
  assignedUniqueId?: string;
};

type ExperimentWithSSR = BaseExperiment & {
  isSSR: true;
  /**
   * Full pathnames or glob patterns where the experiment should run.
   * Examples: `/path/to/page`, `/path/to/*`
   * If you want to run the experiment on all paths, you can provide `**`
   */
  pathnames?: readonly string[];
  /**
   * Full pathnames or glob patterns where the experiment should NOT run.
   * Examples: `/path/to/excluded/page`, `/path/to/excluded/*`
   */
  pathnamesIgnore?: readonly string[];
} & (
    | { pathnames: readonly string[]; pathnamesIgnore?: readonly string[] }
    | { pathnames?: readonly string[]; pathnamesIgnore: readonly string[] }
  );

type ExperimentWithoutSSR = BaseExperiment & {
  isSSR?: false;
  /**
   * Full pathnames or glob patterns where the experiment should run.
   * Examples: `/path/to/page`, `/path/to/*`
   * If you want to run the experiment on all paths, you can provide `**`
   */
  pathnames?: readonly string[];
  /**
   * Full pathnames or glob patterns where the experiment should NOT run.
   * Examples: `/path/to/excluded/page`, `/path/to/excluded/*`
   */
  pathnamesIgnore?: readonly string[];
};

export type Experiment = ExperimentWithSSR | ExperimentWithoutSSR;

export interface SimpleFeatureFlag {
  defaultValue: FeatureFlagValue | boolean;
}

export interface FeatureFlagWithVariants {
  defaultValue: FeatureFlagValue | boolean;
  variants: readonly FeatureFlagValue[];
}

export interface FeatureFlagWithExperiment extends FeatureFlagWithVariants {
  experiment: Experiment;
}

export type Environment = _Environment | `sandbox-${number}`;

export interface EnabledForConfig {
  enabledForEnvironments?: readonly Environment[];
}

// Only support simple feature flag for now
export type FeatureToggleConfig = (SimpleFeatureFlag | FeatureFlagWithVariants | FeatureFlagWithExperiment) &
  EnabledForConfig;

export type FeatureFlagOverride = Record<AvailableFeatureNames, SimpleFeatureFlag>;

export interface FeatureFlagSetup {
  configOverride?: ConfigOverride;
}

export function isFeatureFlagWithVariants(config: FeatureToggleConfig): config is FeatureFlagWithVariants {
  return 'variants' in config;
}

export function isFeatureFlagWithExperiment(config: FeatureToggleConfig): config is FeatureFlagWithExperiment {
  return 'experiment' in config;
}

export type InitCallback = {
  onError?: (props: { error: Error; source: InitResponse['source']; gb: GrowthBook }) => void;
  onInit?: (props: { source: InitResponse['source'] }) => void;
};

export type ExperimentEventBody<ExperimentKey extends string = string, ExperimentVariant = string> = Record<
  ExperimentKey,
  ExperimentVariant
>;

export type EventPayload = AbTestEvent;

export type GrowthbookType = GrowthBook<Record<AvailableFeatureNames, FeatureFlagValue>>;
